import { QWebChannel } from "qwebchannel"
import { useVistCaseStore } from "../stores/vistCaseStore"

interface QtWindow {
  qt?: QtBrowser
}

interface QtBrowser {
  webChannelTransport: any
}

interface QtVistCaseManager {
  json: string
  refreshAvailableCases(): void
  downloadCase(jobId: string, signedUrl: string): void
  runCase(jobId: string, sourceUrl: string): void
  checkVersionSelectorCompatibility(caseFilePath: string): void
  getDownloadPath(): string
  startScanning(): void
  stopScanning(): void
  jsonChanged: QtSignal<() => void>
  caseDownloadProgress: QtSignal<(jobId: string, percentage: number) => void>
  caseDownloadCompleted: QtSignal<(jobId: string, success: boolean) => void>
  caseRunResult: QtSignal<(jobId: string, success: boolean, error: string) => void>
  setUserId(userId: string): void
}

interface QtSignal<F> {
  connect(callback: F): void
  disconnect(callback: F): void
}

interface QtChannel {
  objects: {
    vistCaseManager: QtVistCaseManager
  }
}

class QtConnectionManager {
  private qtManager: QtVistCaseManager | null = null
  private isConnecting = false
  private signalCleanup: Array<() => void> = []

  async connect(): Promise<QtVistCaseManager> {
    if (this.qtManager) {
      return this.qtManager
    }

    if (this.isConnecting) {
      // Wait for existing connection attempt
      while (this.isConnecting) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      if (this.qtManager) return this.qtManager
    }

    this.isConnecting = true

    try {
      const qt = (window as QtWindow).qt || (window.parent as QtWindow).qt
      if (!qt) throw new Error("Not connected to Qt")

      return new Promise((resolve, reject) => {
        new QWebChannel(qt.webChannelTransport, (channel: QtChannel) => {
          try {
            const vistCaseManager = channel.objects.vistCaseManager
            if (!vistCaseManager) {
              reject(new Error("vistCaseManager object not found in Qt channel"))
              return
            }

            this.setupErrorHandling(channel)
            this.connectSignals(vistCaseManager)
            this.qtManager = vistCaseManager

            // Update store
            useVistCaseStore.getState().setQtConnected(true)
            useVistCaseStore.getState().setQtError(null)

            this.isConnecting = false
            console.log('Global Qt connection established')
            resolve(vistCaseManager)
          } catch (error) {
            this.isConnecting = false
            reject(error)
          }
        })
      })
    } catch (error) {
      this.isConnecting = false
      useVistCaseStore.getState().setQtError(error as string)
      useVistCaseStore.getState().setQtConnected(false)
      throw error
    }
  }

  private setupErrorHandling(channel: any) {
    if (channel.execCallbacks) {
      const originalHandleResponse = channel.handleResponse
      channel.handleResponse = function(message: any) {
        try {
          if (originalHandleResponse && typeof originalHandleResponse === 'function') {
            return originalHandleResponse.call(this, message)
          }
        } catch (error) {
          console.warn('QWebChannel callback error (handled):', error)
        }
      }
    }
  }

  private connectSignals(manager: QtVistCaseManager) {
    const store = useVistCaseStore.getState()

    // Clear previous cleanup functions
    this.signalCleanup.forEach(cleanup => {
      try { cleanup() } catch (err) { console.warn('Error during signal cleanup:', err) }
    })
    this.signalCleanup = []

    // JSON changed handler
    const jsonChangedHandler = () => {
      try {
        if (!manager || typeof manager.json !== 'string') return
        const data = JSON.parse(manager.json)
        store.setLocalCases(data.localCases)
        store.setLocalScanComplete(true)
      } catch (error) {
        console.error('Failed to parse Qt data:', error)
      }
    }

    // Download progress handler
    const downloadProgressHandler = (jobId: string, percentage: number) => {
      store.setDownloadProgress(jobId, percentage)
      store.addDownloadingCase(jobId)
    }

    // Download completed handler
    const downloadCompletedHandler = (jobId: string, success: boolean) => {
      console.log(`Download completed for ${jobId}: ${success}`)
      store.removeDownloadingCase(jobId)
      if (success) {
        setTimeout(() => manager.refreshAvailableCases(), 500)
      }
    }

    // Case run result handler
    const caseRunResultHandler = (jobId: string, success: boolean, error: string) => {
      if (success) {
        console.log(`Successfully launched case: ${jobId}`)
      } else {
        console.error(`Failed to launch case ${jobId}:`, error)
      }
      store.removeRunningCase(jobId)
    }

    // Connect signals
    try {
      if (manager.jsonChanged?.connect) {
        manager.jsonChanged.connect(jsonChangedHandler)
        this.signalCleanup.push(() => manager.jsonChanged?.disconnect(jsonChangedHandler))
      }
      if (manager.caseDownloadProgress?.connect) {
        manager.caseDownloadProgress.connect(downloadProgressHandler)
        this.signalCleanup.push(() => manager.caseDownloadProgress?.disconnect(downloadProgressHandler))
      }
      if (manager.caseDownloadCompleted?.connect) {
        manager.caseDownloadCompleted.connect(downloadCompletedHandler)
        this.signalCleanup.push(() => manager.caseDownloadCompleted?.disconnect(downloadCompletedHandler))
      }
      if (manager.caseRunResult?.connect) {
        manager.caseRunResult.connect(caseRunResultHandler)
        this.signalCleanup.push(() => manager.caseRunResult?.disconnect(caseRunResultHandler))
      }
    } catch (error) {
      console.warn('Failed to connect some Qt signals:', error)
    }

    // Get initial data
    try {
      const data = JSON.parse(manager.json)
      store.setLocalCases(data.localCases)
      store.setLocalScanComplete(true)
    } catch (error) {
      console.error('Failed to get initial Qt data:', error)
      store.setLocalScanComplete(true)
    }
  }

  getManager(): QtVistCaseManager | null {
    return this.qtManager
  }

  isConnected(): boolean {
    return !!this.qtManager
  }

  // Don't disconnect - keep connection alive for the entire app session
  // Only disconnect would be on app shutdown
}

export const qtConnectionManager = new QtConnectionManager()
